/**
 * AppSummaryCard Component
 *
 * Reusable component for displaying app KPIs in both Home and App Overview pages.
 *
 * Props:
 * - app: { app_id, app_name, kpis }
 * - environment: string
 * - timeWindow: string
 * - showAppName: true (Home) shows clickable app name header, false (Overview) hides it
 * - showBorder: true (Home) shows card border/padding, false (Overview) shows plain grid
 *
 * Displays 8 KPI cards: Sessions, Traces, Positive, Negative, Agentic, Safety, Cost, Latency
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import KPICard from './KPICard';
import { palette, components } from '../utils/designTokens';
import { getPercentileLatencyTooltip } from '../utils/tooltips';

function AppSummaryCard({ app, environment, timeWindow, showAppName = false, showBorder = false, extraCards = [] }) {
  const navigate = useNavigate();

  if (!app) {
    throw new Error('AppSummaryCard requires app prop');
  }

  const { app_id, app_name, kpis } = app;
  const classifierDelta = (group) => (
    kpis?.classifiers?.[group]?.delta_previous_period
    ?? kpis?.classifiers?.[group]?.delta_1h
  );
  const previousWindowTooltip = (value) => (
    value == null ? 'Comparison unavailable' : 'Change vs previous window'
  );
  const sessionsDelta = kpis?.activity?.sessions_delta_previous_period ?? kpis?.activity?.new_sessions_1h;
  const tracesDelta = kpis?.activity?.traces_delta_previous_period ?? kpis?.activity?.new_traces_1h;
  const costDelta = kpis?.cost?.delta_cost_previous_period_usd ?? kpis?.cost?.delta_cost_1h_usd;
  const latencyDelta = kpis?.latency?.p95_delta_previous_period ?? kpis?.latency?.p95_1h;

  // Signal group navigation handler
  const handleSignalGroupClick = (signalGroup) => {
    navigate(`/app/${app_id}/traces?env=${environment}&range=${timeWindow}&signalGroup=${signalGroup}`);
  };

  // App name click handler (only used when showAppName=true)
  const handleAppNameClick = (e) => {
    e.stopPropagation();
    navigate(`/app/${app_id}?tab=overview&env=${environment}&range=${timeWindow}`);
  };

  // KPI Cards grid
  const cards = [
    /* 1. Sessions */
    <KPICard
      key="sessions"
      label="Sessions"
      icon="agents"
      delta={sessionsDelta}
      total={kpis?.activity?.total_sessions}
      deltaTooltip={previousWindowTooltip(sessionsDelta)}
      totalTooltip="Total user sessions"
      onClick={() => navigate(`/app/${app_id}/sessions?env=${environment}&range=${timeWindow}`)}
    />,
    /* 2. Traces */
    <KPICard
      key="traces"
      label="Traces"
      icon="traces"
      delta={tracesDelta}
      total={kpis?.activity?.total_traces}
      deltaTooltip={previousWindowTooltip(tracesDelta)}
      totalTooltip="Total traces executed"
      onClick={() => navigate(`/app/${app_id}/traces?env=${environment}&range=${timeWindow}`)}
    />,
    /* 3. Positive */
    <KPICard
      key="positive"
      label="Positive"
      icon="quality"
      delta={classifierDelta('positive')}
      total={kpis?.classifiers?.positive?.total}
      deltaTooltip="Change vs previous window"
      totalTooltip="Total positive signals"
      onClick={() => handleSignalGroupClick('positive')}
    />,
    /* 4. Negative */
    <KPICard
      key="negative"
      label="Negative"
      icon="quality"
      delta={classifierDelta('negative')}
      total={kpis?.classifiers?.negative?.total}
      deltaTooltip="Change vs previous window"
      totalTooltip="Total negative signals"
      onClick={() => handleSignalGroupClick('negative')}
    />,
    /* 5. Agentic */
    <KPICard
      key="agentic"
      label="Agentic"
      icon="quality"
      delta={classifierDelta('agentic')}
      total={kpis?.classifiers?.agentic?.total}
      deltaTooltip="Change vs previous window"
      totalTooltip="Total agentic issues"
      onClick={() => handleSignalGroupClick('agentic')}
    />,
    /* 6. Safety */
    <KPICard
      key="safety"
      label="Safety"
      icon="security"
      delta={classifierDelta('safety')}
      total={kpis?.classifiers?.safety?.total}
      deltaTooltip="Change vs previous window"
      totalTooltip="Total safety violations"
      onClick={() => handleSignalGroupClick('safety')}
    />,
    /* 7. Cost */
    <KPICard
      key="cost"
      label="Cost"
      icon="optimization"
      delta={costDelta}
      total={kpis?.cost?.total_cost_usd}
      formatDelta={(v) => v != null ? `$${v.toFixed(4)}` : '-'}
      formatTotal={(v) => v != null ? `$${v.toFixed(4)}` : '-'}
      deltaTooltip={previousWindowTooltip(costDelta)}
      totalTooltip="Total spend"
      onClick={() => navigate(`/app/${app_id}?tab=cost&env=${environment}&range=${timeWindow}`)}
    />,
    /* 8. P95 Latency */
    <KPICard
      key="p95-latency"
      label="P95 Latency"
      icon="performance"
      delta={latencyDelta}
      total={kpis?.latency?.p95_total}
      formatDelta={(v) => v != null ? `${(v / 1000).toFixed(3)}s` : '-'}
      formatTotal={(v) => v != null ? `${(v / 1000).toFixed(3)}s` : '-'}
      deltaTooltip={previousWindowTooltip(latencyDelta)}
      totalTooltip={getPercentileLatencyTooltip()}
      onClick={() => navigate(`/app/${app_id}?tab=performance&env=${environment}&range=${timeWindow}`)}
    />,
    ...extraCards,
  ].filter(Boolean);

  const kpiCardsGrid = (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cards.length}, 1fr)` }}>
      {cards}
    </div>
  );

  // If showBorder=false, return plain grid (for App Overview)
  if (!showBorder) {
    return (
      <>
        {showAppName && (
          <div className="mb-4">
            <h3
              onClick={handleAppNameClick}
              style={{
                color: palette.accents.primary.base,
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none'
              }}
              className="hover:underline"
            >
              {app_name}
            </h3>
          </div>
        )}
        {kpiCardsGrid}
      </>
    );
  }

  // If showBorder=true, return card with border/padding (for Home)
  return (
    <div
      className="rounded-lg p-6"
      style={{
        backgroundColor: components.card.backgroundColor,
        border: `1px solid ${components.card.borderColor}`
      }}
    >
      {showAppName && (
        <div className="mb-4">
          <h3
            onClick={handleAppNameClick}
            style={{
              color: palette.accents.primary.base,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              textDecoration: 'none'
            }}
            className="hover:underline"
          >
            {app_name}
          </h3>
        </div>
      )}
      {kpiCardsGrid}
    </div>
  );
}

export default AppSummaryCard;
